import React, { useMemo } from 'react';
import { buildRadioRowSelection } from 'components/RecordsListView/buildRadioRowSelection';
import InvoiceRowItem from '../components/InvoiceRowItem';
import { InvoiceSummary } from '../types';

export interface UseInvoiceListViewColumnsProps {
  selectedId: string | null;
  onSelectId: (id: string | null) => void;
  searchTerm?: string;
  memberRole?: string;
  memberFinancialAccess?: string;
}

/**
 * Builds the single-column table config + radio `rowSelection` for the invoice
 * `ListView`. Mirrors the shared `useListViewColumns`, rendering `InvoiceRowItem`
 * with the masked-cost props. Rows are keyed by `invoiceId`.
 */
const useInvoiceListViewColumns = ({
  selectedId,
  onSelectId,
  searchTerm = '',
  memberRole,
  memberFinancialAccess
}: UseInvoiceListViewColumnsProps) => {
  const rowSelection = useMemo(
    () =>
      buildRadioRowSelection<InvoiceSummary>({
        selectedId,
        onSelectId,
        getKey: record => record.invoiceId,
        namePrefix: 'invoice'
      }),
    [selectedId, onSelectId]
  );

  const columns = useMemo(
    () => [
      {
        dataIndex: 'title',
        key: 'title',
        render: (_: unknown, record: InvoiceSummary) => (
          <InvoiceRowItem
            invoice={record}
            isSelected={selectedId === record.invoiceId}
            searchTerm={searchTerm}
            memberRole={memberRole}
            memberFinancialAccess={memberFinancialAccess}
          />
        )
      }
    ],
    [selectedId, searchTerm, memberRole, memberFinancialAccess]
  );

  return { rowSelection, columns };
};

export default useInvoiceListViewColumns;
